import { useCallback, useMemo, useRef } from "react";
import type { SimulatorTabInput } from "@/lib/api/simulator-tabs";
import { sortByOrder } from "@/lib/strategy/tabs";
import { deepEqual } from "@/lib/utils";
import { useSavedStrategies } from "@/hooks/useSavedStrategies";
import { useSimulatorTabs } from "@/hooks/useSimulatorTabs";
import type { SavedStrategy, SimulatorTab } from "@/types/api";
import type { StrategyLayer } from "@/types/strategy";
import type { ScenarioContext, Template } from "@/types/template";

// True iff any field in `input` differs from `current`. Mirrors the
// legacy `useTemplates.hasActualChanges` short-circuit so idle re-renders
// don't fire no-op PATCHes that race with in-flight slider edits.
function hasActualChanges(
  current: Template,
  input: {
    name?: string;
    layers?: StrategyLayer[];
    scenario?: ScenarioContext;
    notes?: string;
    tags?: string[];
    archived_at?: string | null;
    ticker?: string;
  },
): boolean {
  if (input.name !== undefined && !deepEqual(input.name, current.name))
    return true;
  if (input.layers !== undefined && !deepEqual(input.layers, current.layers))
    return true;
  if (
    input.scenario !== undefined &&
    !deepEqual(input.scenario, current.scenario)
  )
    return true;
  if (input.notes !== undefined && !deepEqual(input.notes, current.notes))
    return true;
  if (input.tags !== undefined && !deepEqual(input.tags, current.tags))
    return true;
  if (
    input.archived_at !== undefined &&
    !deepEqual(input.archived_at, current.archived_at)
  )
    return true;
  if (input.ticker !== undefined && !deepEqual(input.ticker, current.ticker))
    return true;
  return false;
}

// Bridge: legacy `useTemplates` returned a unified `Template[]` from a
// single legacy `templates` table. The rewrite split that into two
// tables — `simulator_tabs` (the open-tab state) and `saved_strategies`
// (the named library of strategies). Tabs with `saved_strategy_id` set
// are "non-draft" templates; tabs without the link are drafts. This
// hook merges the two views back into the legacy shape so the ported
// structure components keep compiling without a Rails change.
//
// Mapping:
//   SimulatorTab + (optional linked SavedStrategy)  →  Template
//     id            ← tab.id
//     name          ← linked.name ?? ""
//     layers        ← tab.layers
//     ticker        ← tab.ticker
//     scenario      ← tab.scenario merged with `draft: !linked`,
//                     `order: tab.position`
//     notes         ← linked.notes ?? null
//     tags          ← linked.tags ?? []
//     archived_at   ← tab.archived_at  (soft-archive on close, mirrors legacy)
//     created_at    ← tab.opened_at
//     updated_at    ← tab.last_active_at
//
// Writes:
//   saveTemplate(name, layers, scenario, ticker?, notes?, tags?)
//     → if name is non-empty, create a SavedStrategy first, link the
//       new SimulatorTab to it; otherwise open a draft tab.
//   updateTemplate(id, patch)
//     → tabs.update(id, patch); if patch.name and the tab has a
//       linked saved strategy, propagate name/notes/tags to it.
//   deleteTemplate(id)
//     → tabs.close(id). The linked SavedStrategy stays — closing a
//       tab is not a deletion.
//   archiveTemplate / unarchiveTemplate
//     → soft-archive via `simulator_tabs.archived_at`. Archived tabs
//       leave the open-tab strip but stay in the saved-strategies
//       picker for re-opening. Mirrors legacy `templates.archived_at`.
//
// The batch* operations short-circuit through sequential single calls
// — fine for the simulator's typical batch sizes (≤ a few tabs at a
// time).

// `order` is a derived field — its source of truth is `simulator_tabs.position`.
// Strip it from the persisted scenario jsonb and route it to the column.
//
// `draft` looks similar but is NOT derived from `saved_strategy_id`. The
// saved-strategies fetch can lag the tabs fetch on cold page load; if we
// inferred `draft` from `linked === null`, the active tab would briefly
// render as a draft (showing the picker) before the saved strategy
// resolves. tabToTemplate explicitly reads `tab.scenario.draft === true`
// to avoid that flicker, so `draft` MUST persist into `scenario` jsonb.
// Stripping it here would have every createDraft round-trip lose its
// draft flag — the picker would never show on a fresh "+" click.
function splitScenario(scenario: ScenarioContext | undefined): {
  scenario: Record<string, unknown> | undefined;
  position: number | undefined;
} {
  if (scenario === undefined)
    return { scenario: undefined, position: undefined };
  const { order, ...rest } = scenario as ScenarioContext & {
    [k: string]: unknown;
  };
  return { scenario: rest, position: order };
}

function tabToTemplate(
  tab: SimulatorTab,
  saved: readonly SavedStrategy[],
): Template {
  const linked = tab.saved_strategy_id
    ? (saved.find((s) => s.id === tab.saved_strategy_id) ?? null)
    : null;

  // Draft state must derive from the tab's own fields, NOT from whether
  // `linked` resolved. Saved-strategies fetch can lag behind tabs on cold
  // page load; if we set draft=true while waiting for the lookup to
  // hydrate, the simulator passes [] as templateLayers to loadTab, the
  // store records loadedTabId, and the subsequent (correct) re-render
  // can't reseed layers because loadTab early-returns on same tabId. The
  // user-visible symptom is the active-on-load tab rendering empty until
  // they tab away and back. Source of truth: tab.scenario.draft === true
  // for explicit drafts (createDraft sets this), otherwise non-draft.
  const baseScenario: Record<string, unknown> = tab.scenario ?? {};
  const scenario: ScenarioContext = {
    ...baseScenario,
    draft: baseScenario.draft === true,
    order: tab.position,
  };

  return {
    id: tab.id,
    saved_strategy_id: tab.saved_strategy_id ?? null,
    name: linked?.name ?? "",
    layers: tab.layers as StrategyLayer[],
    scenario,
    notes: linked?.notes ?? null,
    tags: linked?.tags ? [...linked.tags] : [],
    archived_at: tab.archived_at,
    ticker: tab.ticker,
    created_at: linked?.created_at ?? tab.opened_at,
    updated_at: linked?.updated_at ?? tab.last_active_at,
  };
}

export function useTemplates() {
  const tabsCtx = useSimulatorTabs();
  const savedCtx = useSavedStrategies();
  const loading =
    tabsCtx.status === "loading" ||
    tabsCtx.status === "idle" ||
    savedCtx.status === "loading" ||
    savedCtx.status === "idle";

  // All tabs (active + archived), mapped to Templates. The split into
  // `templates` (open tabs) and `archived` (closed tabs picker entries)
  // happens via archived_at — same shape legacy callers expect.
  const allTemplates: Template[] = useMemo(() => {
    const merged = tabsCtx.tabs.map((tab) =>
      tabToTemplate(tab, savedCtx.strategies),
    );
    return sortByOrder(merged);
  }, [tabsCtx.tabs, savedCtx.strategies]);
  const templates = useMemo(
    () => allTemplates.filter((t) => !t.archived_at),
    [allTemplates],
  );

  const saveTemplate = useCallback(
    async (input: {
      name: string;
      layers: StrategyLayer[];
      scenario: ScenarioContext;
      notes?: string;
      tags?: string[];
      archived_at?: string | null;
      ticker?: string;
    }): Promise<Template | null> => {
      const ticker = input.ticker ?? "SPX";
      let savedId: string | null = null;
      let createdSaved: SavedStrategy | null = null;
      if (input.name.trim()) {
        try {
          const created = await savedCtx.create({
            name: input.name.trim(),
            ticker,
            layers: input.layers,
            notes: input.notes ?? null,
            tags: input.tags ?? [],
          });
          createdSaved = created;
          savedId = created.id;
        } catch {
          return null;
        }
      }
      const { scenario: cleanScenario, position } = splitScenario(
        input.scenario,
      );
      try {
        const tab = await tabsCtx.open({
          ticker,
          layers: input.layers,
          scenario: cleanScenario,
          ...(position !== undefined ? { position } : {}),
          saved_strategy_id: savedId,
        });
        const savedStrategies = createdSaved
          ? [createdSaved, ...savedCtx.strategies]
          : savedCtx.strategies;
        return tabToTemplate(tab, savedStrategies);
      } catch {
        return null;
      }
    },
    [savedCtx, tabsCtx],
  );

  const updateTemplate = useCallback(
    async (
      id: string,
      input: {
        name?: string;
        layers?: StrategyLayer[];
        scenario?: ScenarioContext;
        notes?: string;
        tags?: string[];
        archived_at?: string | null;
        ticker?: string;
      },
    ): Promise<Template | null> => {
      // ── Legacy guards (palace-grounded) ──
      // (1) Never overwrite a non-draft template's `layers` with `[]`.
      // Tab-switch races briefly set local layers to [] before the new
      // template hydrates; without this guard, the debounced auto-save
      // fires `layers: []` to the saved tab and nukes it.
      if (input.layers !== undefined && input.layers.length === 0) {
        const existing = allTemplates.find((t) => t.id === id);
        if (!existing?.scenario.draft) {
          const { layers: _dropped, ...rest } = input;
          if (Object.keys(rest).length === 0) return null;
          input = rest;
        }
      }

      // (2) `has_changes: true` requires `saved_state` to exist.
      // `saved_state.layers` may be empty — that encodes the
      // freshly-anchored "discard means delete" sentinel. Only coerce when
      // saved_state itself is missing: inherit from the cached template,
      // or downgrade `has_changes` to false so a later closeTab(id, false)
      // doesn't silently lose the dirty edits.
      if (input.scenario?.has_changes && !input.scenario.saved_state) {
        const current = allTemplates.find((t) => t.id === id);
        const existingSnap = current?.scenario.saved_state;
        if (existingSnap) {
          input = {
            ...input,
            scenario: { ...input.scenario, saved_state: existingSnap },
          };
        } else {
          input = {
            ...input,
            scenario: { ...input.scenario, has_changes: false },
          };
        }
      }

      // (3) Deep-equality short-circuit. Idle re-renders that happen to
      // hit `updateTemplate` would otherwise fire a no-op PATCH that
      // round-trips and risks clobbering in-flight edits.
      const currentTemplate = allTemplates.find((t) => t.id === id);
      if (currentTemplate && !hasActualChanges(currentTemplate, input))
        return currentTemplate;

      const tabPatch: Partial<SimulatorTabInput> = {};
      if (input.layers !== undefined) tabPatch.layers = input.layers;
      if (input.scenario !== undefined) {
        const { scenario: cleanScenario, position } = splitScenario(
          input.scenario,
        );
        tabPatch.scenario = cleanScenario;
        if (position !== undefined) {
          const current = tabsCtx.tabs.find((t) => t.id === id);
          if (!current || current.position !== position)
            tabPatch.position = position;
        }
      }
      if (input.ticker !== undefined) tabPatch.ticker = input.ticker;
      if (input.archived_at !== undefined) {
        tabPatch.archived_at = input.archived_at;
      }

      let updatedTab: SimulatorTab | null;
      if (Object.keys(tabPatch).length > 0) {
        try {
          updatedTab = await tabsCtx.update(id, tabPatch);
        } catch {
          return null;
        }
      } else {
        updatedTab = tabsCtx.tabs.find((t) => t.id === id) ?? null;
      }
      if (!updatedTab) return null;

      // Propagate name / notes / tags to the linked SavedStrategy if
      // the tab is non-draft. A name patch on a draft tab is the
      // "anchor this tab" gesture — create a SavedStrategy and link.
      if (
        (input.name !== undefined ||
          input.notes !== undefined ||
          input.tags !== undefined) &&
        updatedTab.saved_strategy_id
      ) {
        try {
          await savedCtx.update(updatedTab.saved_strategy_id, {
            name: input.name,
            notes: input.notes ?? undefined,
            tags: input.tags,
          });
        } catch {
          /* leave the tab patch in place */
        }
      } else if (
        input.name !== undefined &&
        input.name.trim() !== "" &&
        !updatedTab.saved_strategy_id
      ) {
        try {
          const created = await savedCtx.create({
            name: input.name.trim(),
            ticker: updatedTab.ticker,
            layers: updatedTab.layers as unknown[],
            notes: input.notes ?? null,
            tags: input.tags ?? [],
          });
          updatedTab = await tabsCtx.update(id, {
            saved_strategy_id: created.id,
          });
        } catch {
          /* keep the tab as-is */
        }
      }

      return tabToTemplate(updatedTab, savedCtx.strategies);
    },
    [tabsCtx, savedCtx, allTemplates],
  );

  const deleteTemplate = useCallback(
    async (id: string) => {
      try {
        await tabsCtx.close(id);
      } catch {
        /* swallow — optimistic UI is unchanged */
      }
    },
    [tabsCtx],
  );

  // Archived templates = closed tabs in the picker. Soft-archived
  // SimulatorTab rows with archived_at populated. Legacy archived
  // rows arrive here via `seed_legacy:templates_and_tabs`, which (after
  // the fix) creates a real SimulatorTab with archived_at set instead
  // of dropping it on import — so original timestamps, scenario,
  // pinned, position all survive the cutover.
  const archived = useMemo<Template[]>(
    () => allTemplates.filter((t) => Boolean(t.archived_at)),
    [allTemplates],
  );
  const drafts = useMemo(
    () => templates.filter((t) => t.scenario.draft),
    [templates],
  );

  const archiveTemplate = useCallback(
    (id: string) =>
      updateTemplate(id, { archived_at: new Date().toISOString() }),
    [updateTemplate],
  );

  const unarchiveTemplate = useCallback(
    (id: string) => updateTemplate(id, { archived_at: null }),
    [updateTemplate],
  );

  const batchCreate = useCallback(
    async (
      inputs: {
        name: string;
        layers: unknown[];
        scenario: Record<string, unknown>;
        notes?: string | null;
        tags?: string[];
        archived_at?: string | null;
        ticker?: string;
      }[],
    ): Promise<Template[]> => {
      // Inputs with a non-empty name still need a SavedStrategy to anchor
      // the name/notes/tags. Create those first (parallel — distinct
      // resource), then a single batch_create for all the tabs in one
      // round-trip. Compared with the old path (2N HTTP calls fanning
      // out to per-id endpoints) this is at most N + 1 in the worst
      // case, and exactly 1 when no inputs carry a name.
      const savedIds = await Promise.all(
        inputs.map(async (input) => {
          const name = input.name.trim();
          if (!name) return null;
          try {
            const created = await savedCtx.create({
              name,
              ticker: input.ticker ?? "SPX",
              layers: input.layers,
              notes: input.notes ?? null,
              tags: input.tags ?? [],
            });
            return created.id;
          } catch {
            return null;
          }
        }),
      );

      const tabInputs: SimulatorTabInput[] = inputs.map((input, i) => {
        const { scenario: cleanScenario, position } = splitScenario(
          input.scenario as unknown as ScenarioContext,
        );
        return {
          ticker: input.ticker ?? "SPX",
          layers: input.layers,
          scenario: cleanScenario,
          ...(position !== undefined ? { position } : {}),
          saved_strategy_id: savedIds[i],
          archived_at: input.archived_at ?? null,
        };
      });

      try {
        const created = await tabsCtx.batchCreate(tabInputs);
        return created.map((tab) => tabToTemplate(tab, savedCtx.strategies));
      } catch {
        return [];
      }
    },
    [savedCtx, tabsCtx],
  );

  const batchUpdate = useCallback(
    async (
      updates: {
        id: string;
        archived_at?: string | null;
        scenario?: Record<string, unknown>;
        layers?: StrategyLayer[];
        name?: string;
      }[],
      _optimistic?: (t: Template) => Template,
    ) => {
      // Tab-level fields collapse into a single batch_update round-trip.
      // `name` still flows through `updateTemplate` per-id because it
      // crosses into saved_strategies — call sites that pass `name` in
      // the batch (none today) get the slower path on purpose.
      const tabPatches: ({ id: string } & Partial<SimulatorTabInput>)[] = [];
      const namePatches: typeof updates = [];
      for (const u of updates) {
        if (u.name !== undefined) {
          namePatches.push(u);
          continue;
        }
        const patch: { id: string } & Partial<SimulatorTabInput> = { id: u.id };
        if (u.layers !== undefined) patch.layers = u.layers;
        if (u.scenario !== undefined) {
          const { scenario: cleanScenario, position } = splitScenario(
            u.scenario as unknown as ScenarioContext,
          );
          patch.scenario = cleanScenario;
          if (position !== undefined) {
            const current = tabsCtx.tabs.find((t) => t.id === u.id);
            if (!current || current.position !== position)
              patch.position = position;
          }
        }
        if (u.archived_at !== undefined) patch.archived_at = u.archived_at;
        tabPatches.push(patch);
      }

      if (tabPatches.length > 0) {
        try {
          await tabsCtx.batchUpdate(tabPatches);
        } catch {
          /* swallow — optimistic UI is unchanged */
        }
      }
      if (namePatches.length > 0) {
        await Promise.all(
          namePatches.map((u) =>
            updateTemplate(u.id, {
              name: u.name,
              scenario: u.scenario as unknown as ScenarioContext | undefined,
              archived_at: u.archived_at,
            }),
          ),
        );
      }
    },
    [tabsCtx, updateTemplate],
  );

  const batchDelete = useCallback(
    async (ids: string[]) => {
      try {
        await tabsCtx.batchDestroy(ids);
      } catch {
        /* swallow — caller's optimistic UI handles re-render */
      }
    },
    [tabsCtx],
  );

  // Pure-position bulk write — one round-trip to `POST /reorder`, one SQL
  // UPDATE on the backend. Use this instead of `batchUpdate({ scenario:
  // { order } })` whenever the only thing changing is tab ordering.
  const reorder = useCallback(
    (ids: string[]) => tabsCtx.reorder(ids),
    [tabsCtx],
  );

  // Latest-value refs so `revalidate` keeps a stable identity across
  // context re-renders. Assignment-in-render is intentional here.
  const tabsCtxRef = useRef(tabsCtx);
  // eslint-disable-next-line react-hooks/refs
  tabsCtxRef.current = tabsCtx;
  const savedCtxRef = useRef(savedCtx);
  // eslint-disable-next-line react-hooks/refs
  savedCtxRef.current = savedCtx;
  const revalidate = useCallback(() => {
    void tabsCtxRef.current.reload();
    void savedCtxRef.current.reload();
  }, []);

  return {
    loading,
    templates,
    archived,
    drafts,
    saveTemplate,
    updateTemplate,
    deleteTemplate,
    archiveTemplate,
    unarchiveTemplate,
    batchUpdate,
    batchCreate,
    batchDelete,
    reorder,
    revalidate,
  };
}
